import { Language, supportedLanguages, defaultLanguage } from '../translations';

/**
 * Detects language from URL path
 * Supports paths like /en, /pt, /pt-BR, /es, etc.
 */
export const detectLanguageFromPath = (): Language => {
  if (typeof window === 'undefined') {
    return defaultLanguage;
  }

  const path = window.location.pathname;
  const pathSegments = path.split('/').filter(Boolean);
  
  if (pathSegments.length > 0) {
    // Handle pt-BR with hyphen
    const firstSegment = pathSegments[0].toLowerCase();
    // Check for pt-BR first (longer match)
    if (firstSegment === 'pt-br' && supportedLanguages.includes('pt-BR')) {
      return 'pt-BR';
    }
    // Then check other languages
    const lang = firstSegment as Language;
    if (supportedLanguages.includes(lang)) {
      return lang;
    }
  }

  return defaultLanguage;
};

/**
 * Detects language from browser settings
 */
export const detectLanguageFromBrowser = (): Language => {
  if (typeof window === 'undefined') {
    return defaultLanguage;
  }

  const browserLang = navigator.language || (navigator as any).userLanguage;
  const langLower = browserLang.toLowerCase();
  
  // Check for pt-BR first (full match)
  if (langLower.startsWith('pt-br') && supportedLanguages.includes('pt-BR')) {
    return 'pt-BR';
  }
  
  // Check for other languages
  const langCode = langLower.split('-')[0] as Language;
  if (supportedLanguages.includes(langCode)) {
    return langCode;
  }

  return defaultLanguage;
};

/**
 * Gets the preferred language from various sources (static HTML > URL > localStorage > browser > default)
 */
export const getPreferredLanguage = (): Language => {
  if (typeof window === 'undefined') {
    return defaultLanguage;
  }

  // 1. Check if language was set in static HTML (for crawlers/SSR)
  const staticLang = (window as any).__INITIAL_LANGUAGE__;
  if (staticLang && supportedLanguages.includes(staticLang)) {
    return staticLang;
  }

  // 2. Check URL path (highest priority for client-side)
  const urlLang = detectLanguageFromPath();
  if (urlLang !== defaultLanguage || window.location.pathname === '/') {
    // If URL has a language or is root, use URL language
    // For root, we'll use browser detection below
    if (window.location.pathname !== '/' && urlLang !== defaultLanguage) {
      return urlLang;
    }
  }

  // 3. Check localStorage
  const savedLang = localStorage.getItem('preferred-language') as Language;
  if (savedLang && supportedLanguages.includes(savedLang)) {
    return savedLang;
  }

  // 4. Check browser language
  const browserLang = detectLanguageFromBrowser();
  if (browserLang !== defaultLanguage) {
    return browserLang;
  }

  // 5. Default fallback
  return defaultLanguage;
};
